import { checklistClient } from '../../config/api';
import {
  Tag,
  CreateTagRequest,
  UpdateTagRequest,
  PaginatedTagsResponse,
} from '../types/tag.types';
import { apiErrorHandler } from '@/helpers/apiErrorHandler';

export class TagService {
  private readonly basePath = '/api/tags';

  createTag = async (data: CreateTagRequest): Promise<Tag> => {
    try {
      const response = await checklistClient.post<any>(this.basePath, data);

      if (response.data && response.data.id) {
        return response.data as Tag;
      }

      if (response.data && response.data.success && response.data.data) {
        return response.data.data;
      }

      if (response.status === 201) {
        return response.data as Tag;
      }

      throw new Error('Invalid response format');
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error creating tag');
    }
  };

  updateTag = async (id: string, data: UpdateTagRequest): Promise<Tag> => {
    try {
      const response = await checklistClient.put<any>(
        `${this.basePath}/${id}`,
        data
      );

      if (response.data && response.data.id) {
        return response.data as Tag;
      }

      if (response.data && response.data.success && response.data.data) {
        return response.data.data;
      }

      if (response.status === 200) {
        return response.data as Tag;
      }

      throw new Error('Invalid response format');
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error updating tag');
    }
  };

  getTagById = async (id: string): Promise<Tag> => {
    try {
      const response = await checklistClient.get<any>(`${this.basePath}/${id}`);

      if (response.data && response.data.id) {
        return response.data as Tag;
      }

      if (response.data && response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error('Invalid response format');
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error fetching tag');
    }
  };

  getAllTags = async (
    pageNumber: number = 1,
    pageSize: number = 10
  ): Promise<PaginatedTagsResponse> => {
    try {
      const response = await checklistClient.get<any>(
        `${this.basePath}?pageNumber=${pageNumber}&pageSize=${pageSize}`
      );

      if (response.data && response.data.items && Array.isArray(response.data.items)) {
        return {
          items: response.data.items,
          totalCount: response.data.totalCount || 0,
          totalPages: response.data.totalPages || 1,
          pageNumber: response.data.pageNumber || pageNumber,
          pageSize: response.data.pageSize || pageSize,
        };
      }

      if (Array.isArray(response.data)) {
        return {
          items: response.data,
          totalCount: response.data.length,
          totalPages: 1,
          pageNumber: 1,
          pageSize: response.data.length,
        };
      }

      const apiResponse = response.data as any;
      if (apiResponse.success && apiResponse.data) {
        return {
          items: apiResponse.data,
          totalCount: apiResponse.data.length,
          totalPages: 1,
          pageNumber: 1,
          pageSize: apiResponse.data.length,
        };
      }

      throw new Error('Invalid response format');
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error fetching tags');
    }
  };

  getActiveTags = async (
    pageNumber: number = 1,
    pageSize: number = 10
  ): Promise<PaginatedTagsResponse> => {
    try {
      const response = await checklistClient.get<any>(
        `${this.basePath}/active?pageNumber=${pageNumber}&pageSize=${pageSize}`
      );

      if (response.data && response.data.items && Array.isArray(response.data.items)) {
        return {
          items: response.data.items,
          totalCount: response.data.totalCount || 0,
          totalPages: response.data.totalPages || 1,
          pageNumber: response.data.pageNumber || pageNumber,
          pageSize: response.data.pageSize || pageSize,
        };
      }

      if (Array.isArray(response.data)) {
        return {
          items: response.data,
          totalCount: response.data.length,
          totalPages: 1,
          pageNumber: 1,
          pageSize: response.data.length,
        };
      }

      const apiResponse = response.data as any;
      if (apiResponse.success && apiResponse.data) {
        return {
          items: apiResponse.data,
          totalCount: apiResponse.data.length,
          totalPages: 1,
          pageNumber: 1,
          pageSize: apiResponse.data.length,
        };
      }

      throw new Error('Invalid response format');
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error fetching active tags');
    }
  };

  getAllActiveTags = async (): Promise<Tag[]> => {
    try {
      const response = await checklistClient.get<any>(`${this.basePath}/all-active`);

      if (Array.isArray(response.data)) {
        return response.data;
      }

      const apiResponse = response.data as any;
      if (apiResponse.success && apiResponse.data) {
        return apiResponse.data;
      }

      throw new Error('Invalid response format');
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error fetching all active tags');
    }
  };

  activateTag = async (id: string): Promise<void> => {
    try {
      const response = await checklistClient.patch<any>(
        `${this.basePath}/${id}/activate`
      );

      if (response.data && response.data.success === false) {
        throw new Error(response.data?.errorMessage || 'Error activating tag');
      }

      return;
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error activating tag');
    }
  };

  deactivateTag = async (id: string): Promise<void> => {
    try {
      const response = await checklistClient.patch<any>(
        `${this.basePath}/${id}/deactivate`
      );

      if (response.data && response.data.success === false) {
        throw new Error(response.data?.errorMessage || 'Error deactivating tag');
      }

      return;
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error deactivating tag');
    }
  };

  deleteTag = async (id: string): Promise<void> => {
    try {
      const response = await checklistClient.delete<any>(`${this.basePath}/${id}`);

      if (response.data && response.data.success === false) {
        throw new Error(response.data?.errorMessage || 'Error deleting tag');
      }
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error deleting tag');
    }
  };
}

export const tagService = new TagService();
